import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, Heart, Loader2, Plus, Send, ShieldCheck, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { LanguageSwitch } from "@/components/LanguageSwitch";
import { Bloom, Florals } from "@/components/site/Florals";
import { consentText, errorMessage, getRsvpPrefill, getWeddingPublic, submitRsvp } from "@/lib/api";
import { currentLanguage, t, tf } from "@/i18n";

/* The invitation, not a form.

   A guest opens this from a message signed by the couple. It should look like
   the card they would have found in an envelope: their names in a fine serif,
   a hand-written line, white flowers, a lot of air — and then the questions,
   which are the whole point, kept short and asked kindly. */

const EMPTY = {
  first_name: "",
  last_name: "",
  age: "",
  profession: "",
  passions: "",
  attending_with: "",
  dietary: "",
  interests: "",
  seat_near: "",
  avoid: "",
};

const REQUIRED = ["first_name", "last_name", "profession", "passions", "attending_with"];

const SectionTitle = ({ children }) => (
  <div className="flex items-center gap-3 pt-2">
    <span className="h-px flex-1" style={{ backgroundColor: "var(--line)" }} />
    <span className="eyebrow">{children}</span>
    <span className="h-px flex-1" style={{ backgroundColor: "var(--line)" }} />
  </div>
);

const Field = ({ id, label, optional, help, children }) => (
  <div>
    <Label htmlFor={id} className="flex items-baseline gap-2 text-[13.5px] font-semibold">
      {label}
      <span className="text-[11px] font-normal" style={{ color: optional ? "var(--muted-ink)" : "var(--gild)" }}>
        {optional ? t("rsvp.optional") : t("rsvp.required")}
      </span>
    </Label>
    {help ? <p className="mt-0.5 text-[12px] text-[color:var(--muted-ink)]">{help}</p> : null}
    {children}
  </div>
);

const inputClass = "mt-2 h-11 bg-white text-[15px]";

/** Ivory page, florals in two corners, the card centred. Declared at module
    level on purpose: a component created inside render is a new type each
    time, and React would remount the card — and drop focus — on every key. */
const Shell = ({ children }) => (
  <div className="relative min-h-screen overflow-hidden pb-16">
    <Florals corner="top-left" size={320} className="opacity-70" />
    <Florals corner="bottom-right" size={300} className="opacity-60" />
    <main className="relative mx-auto w-full max-w-[640px] px-4 pt-10 sm:pt-14">{children}</main>
  </div>
);

const formatDate = (iso) => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(currentLanguage() === "en" ? "en-GB" : "fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

export default function Rsvp() {
  const { weddingId } = useParams();
  const [params] = useSearchParams();
  // Present when the guest followed their own invitation link rather than a
  // link shared with the whole wedding.
  const guestToken = params.get("t") || "";
  const navigate = useNavigate();
  const [known, setKnown] = useState(null);
  const [wedding, setWedding] = useState(null);
  const [consent, setConsent] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [alone, setAlone] = useState(false);
  const [withChildren, setWithChildren] = useState(null); // null = not answered yet
  const [children, setChildren] = useState([]);

  useEffect(() => {
    let active = true;
    Promise.all([
      getWeddingPublic(weddingId),
      consentText().catch(() => null),
      guestToken ? getRsvpPrefill(weddingId, guestToken).catch(() => null) : null,
    ])
      .then(([data, text, mine]) => {
        if (!active) return;
        setWedding(data);
        setConsent(text);
        if (mine) {
          setKnown(mine);
          const { children: kids, ...fields } = mine.guest;
          setForm((prev) => ({
            ...prev,
            ...Object.fromEntries(
              Object.keys(EMPTY).map((key) => [
                key,
                fields[key] === null || fields[key] === undefined ? prev[key] : String(fields[key]),
              ])
            ),
          }));
          const list = (kids || []).map((kid) => ({
            first_name: kid.first_name || "",
            last_name: kid.last_name || "",
            age: kid.age === null || kid.age === undefined ? "" : String(kid.age),
          }));
          setChildren(list);
          if (mine.already_answered) setWithChildren(list.length > 0);
        }
      })
      .catch(() => active && setWedding(null))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [weddingId, guestToken]);

  const set = (id, value) => setForm((prev) => ({ ...prev, [id]: value }));

  const answered = useMemo(() => {
    const filled = REQUIRED.filter((key) => (key === "attending_with" ? alone : false) || String(form[key]).trim()).length;
    return filled + (withChildren === null ? 0 : 1);
  }, [form, alone, withChildren]);
  const total = REQUIRED.length + 1;

  const setChild = (index, key, value) =>
    setChildren((prev) => prev.map((c, i) => (i === index ? { ...c, [key]: value } : c)));

  const submit = async (event) => {
    event.preventDefault();
    const complaints = [];
    if (!form.first_name.trim()) complaints.push(t("rsvp.needFirstName"));
    if (!form.last_name.trim()) complaints.push(t("rsvp.needLastName"));
    if (!alone && !form.attending_with.trim()) complaints.push(t("rsvp.needCompany"));
    if (withChildren === null) complaints.push(t("rsvp.needChildren"));
    if (withChildren && !children.some((c) => c.first_name.trim())) complaints.push(t("rsvp.needChildName"));
    if (!form.profession.trim()) complaints.push(t("rsvp.needProfession"));
    if (!form.passions.trim()) complaints.push(t("rsvp.needPassions"));
    if (!agreed) complaints.push(t("rsvp.needConsent"));
    if (complaints.length) {
      toast.error(complaints[0]);
      return;
    }
    setSaving(true);
    try {
      const result = await submitRsvp(weddingId, {
        ...form,
        attending_with: alone ? t("rsvp.aloneValue") : form.attending_with,
        age: form.age === "" ? null : Number(form.age),
        with_children: Boolean(withChildren),
        guest_token: guestToken,
        consent: true,
        children: withChildren
          ? children
              .filter((c) => c.first_name.trim())
              .map((c) => ({
                first_name: c.first_name.trim(),
                last_name: c.last_name.trim(),
                age: c.age === "" ? null : Number(c.age),
              }))
          : [],
      });
      setDone(result);
    } catch (error) {
      toast.error(errorMessage(error, t("rsvp.failed")));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-[color:var(--muted-ink)]" />
      </div>
    );
  }

  if (!wedding) {
    return (
      <div className="flex min-h-screen items-center justify-center px-6">
        <div className="surface max-w-md rounded-xl p-8 text-center">
          <h1 className="text-2xl font-semibold">{t("rsvp.unavailable")}</h1>
          <p className="mt-2 text-[13.5px] text-[color:var(--ink-soft)]">{t("rsvp.unavailableHint")}</p>
          <Button className="mt-5" onClick={() => navigate("/")} data-testid="rsvp-home-button">
            {t("rsvp.home")}
          </Button>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <Shell>
        <div className="invite-frame rounded-[4px] p-8 sm:p-10" data-testid="rsvp-thank-you">
          <Bloom size={34} className="mx-auto" />
          <h1 className="font-display mt-4 text-center text-[36px] font-medium leading-tight">
            {tf("rsvp.thanks", { name: form.first_name })}
          </h1>
          <p className="mt-4 text-center text-[14.5px] leading-relaxed text-[color:var(--ink-soft)]">
            {tf("rsvp.arrived", { couple: wedding.couple_names })}
          </p>
          {done.children_added ? (
            <p className="mt-2 text-center text-[13.5px] text-[color:var(--ink-soft)]">
              {tf("rsvp.childrenAdded", { n: done.children_added })}
            </p>
          ) : null}
          <SectionTitle>
            <Check className="h-3.5 w-3.5" />
          </SectionTitle>
          <p className="mt-5 text-center text-[13px] text-[color:var(--muted-ink)]">
            {done.matched ? t("rsvp.matched") : t("rsvp.addedToList")}{" "}
            {guestToken ? t("rsvp.correctLink") : t("rsvp.correctName")}
          </p>
          {guestToken ? null : (
            <div className="mt-6 text-center">
              <Button
                variant="outline"
                onClick={() => {
                  setDone(null);
                  setForm(EMPTY);
                  setChildren([]);
                  setWithChildren(null);
                  setAlone(false);
                  setAgreed(false);
                }}
                data-testid="rsvp-another-button"
              >
                {t("rsvp.another")}
              </Button>
            </div>
          )}
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="mb-3 flex justify-end">
        <LanguageSwitch />
      </div>
      <div className="invite-frame rounded-[4px] p-6 sm:p-10">
        {/* the card itself */}
        <div className="text-center">
          <Bloom size={34} className="mx-auto" />
          <p className="eyebrow mt-3">{t("rsvp.invited")}</p>
          <p className="font-script mt-1 text-[34px] leading-none" style={{ color: "var(--ink-soft)" }}>
            {t("rsvp.to")}
          </p>
          <h1 className="font-display mt-3 text-[40px] font-medium leading-[1.05] tracking-tight sm:text-[48px]">
            {wedding.couple_names}
          </h1>
          {(wedding.venue || wedding.wedding_date) && (
            <p className="mt-3 text-[13px] uppercase tracking-[0.16em] text-[color:var(--muted-ink)]">
              {[wedding.venue, formatDate(wedding.wedding_date)].filter(Boolean).join("  ·  ")}
            </p>
          )}
        </div>

        {known ? (
          <div
            className="mt-6 rounded-lg px-4 py-3"
            style={{ backgroundColor: "var(--paper-2)" }}
            data-testid="rsvp-known-guest"
          >
            <p className="text-[14px] font-medium">{tf("rsvp.greeting", { name: known.guest.first_name })}</p>
            <p className="mt-0.5 text-[13px] leading-relaxed text-[color:var(--ink-soft)]">
              {known.already_answered ? t("rsvp.alreadyAnswered") : t("rsvp.yourLink")}
            </p>
          </div>
        ) : null}

        <p className="mt-6 text-center text-[14px] leading-relaxed text-[color:var(--ink-soft)]">{t("rsvp.intro")}</p>
        <p className="font-num mt-2 text-center text-[11px] text-[color:var(--muted-ink)]" data-testid="rsvp-progress">
          {tf("rsvp.progress", { done: answered, total })}
        </p>

        <form onSubmit={submit} className="mt-6 space-y-5">
          <SectionTitle>{t("rsvp.about")}</SectionTitle>

          <div className="grid grid-cols-2 gap-3">
            <Field id="first_name" label={t("rsvp.firstName")}>
              <Input
                id="first_name"
                value={form.first_name}
                onChange={(e) => set("first_name", e.target.value)}
                placeholder="Sofia"
                className={inputClass}
                data-testid="rsvp-first_name-input"
              />
            </Field>
            <Field id="last_name" label={t("rsvp.lastName")}>
              <Input
                id="last_name"
                value={form.last_name}
                onChange={(e) => set("last_name", e.target.value)}
                placeholder="Marín"
                className={inputClass}
                data-testid="rsvp-last_name-input"
              />
            </Field>
          </div>

          <div className="grid grid-cols-[90px_minmax(0,1fr)] gap-3">
            <Field id="age" label={t("rsvp.age")} optional>
              <Input
                id="age"
                type="number"
                min={0}
                max={120}
                value={form.age}
                onChange={(e) => set("age", e.target.value)}
                placeholder="34"
                className={`font-num ${inputClass}`}
                data-testid="rsvp-age-input"
              />
            </Field>
            <Field id="profession" label={t("rsvp.profession")}>
              <Input
                id="profession"
                value={form.profession}
                onChange={(e) => set("profession", e.target.value)}
                placeholder={t("rsvp.professionPlaceholder")}
                className={inputClass}
                data-testid="rsvp-profession-input"
              />
            </Field>
          </div>

          <Field id="passions" label={t("rsvp.passions")} help={t("rsvp.passionsHint")}>
            <Input
              id="passions"
              value={form.passions}
              onChange={(e) => set("passions", e.target.value)}
              placeholder={t("rsvp.passionsPlaceholder")}
              className={inputClass}
              data-testid="rsvp-passions-input"
            />
          </Field>

          <SectionTitle>{t("rsvp.company")}</SectionTitle>

          <Field id="attending_with" label={t("rsvp.attendingWith")} help={t("rsvp.attendingWithHint")}>
            <Textarea
              id="attending_with"
              value={alone ? "" : form.attending_with}
              onChange={(e) => set("attending_with", e.target.value)}
              placeholder={t("rsvp.attendingWithPlaceholder")}
              rows={2}
              disabled={alone}
              className="mt-2 bg-white text-[14.5px] disabled:opacity-50"
              data-testid="rsvp-attending_with-input"
            />
            <label className="mt-2 flex cursor-pointer items-center gap-2 text-[13px]">
              <input
                type="checkbox"
                checked={alone}
                onChange={(e) => setAlone(e.target.checked)}
                className="h-4 w-4"
                data-testid="rsvp-alone"
              />
              {t("rsvp.alone")}
            </label>
          </Field>

          <div>
            <Label className="flex items-baseline gap-2 text-[13.5px] font-semibold">
              {t("rsvp.childrenQuestion")}
              <span className="text-[11px] font-normal" style={{ color: "var(--gild)" }}>
                {t("rsvp.required")}
              </span>
            </Label>
            <p className="mt-0.5 text-[12px] text-[color:var(--muted-ink)]">{t("rsvp.childrenHint")}</p>
            <div className="mt-2 flex gap-2" role="radiogroup" data-testid="rsvp-children-choice">
              {[
                { value: true, label: t("rsvp.childrenYes") },
                { value: false, label: t("rsvp.childrenNo") },
              ].map((option) => (
                <button
                  key={String(option.value)}
                  type="button"
                  role="radio"
                  aria-checked={withChildren === option.value}
                  onClick={() => {
                    setWithChildren(option.value);
                    if (option.value && !children.length)
                      setChildren([{ first_name: "", last_name: "", age: "" }]);
                  }}
                  data-testid={`rsvp-children-${option.value ? "yes" : "no"}`}
                  className="h-10 rounded-full border px-5 text-[13.5px] font-medium transition-colors"
                  style={{
                    borderColor: withChildren === option.value ? "var(--ink)" : "var(--line)",
                    backgroundColor: withChildren === option.value ? "var(--ink)" : "#ffffff",
                    color: withChildren === option.value ? "var(--paper)" : "var(--ink-soft)",
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>

            {withChildren ? (
              <div className="mt-3 space-y-2">
                {children.map((child, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      value={child.first_name}
                      onChange={(e) => setChild(index, "first_name", e.target.value)}
                      placeholder={t("rsvp.childFirstName")}
                      className="h-10 bg-white"
                      aria-label={`${t("rsvp.childFirstName")} ${index + 1}`}
                      data-testid={`rsvp-child-${index}-first_name`}
                    />
                    <Input
                      value={child.last_name}
                      onChange={(e) => setChild(index, "last_name", e.target.value)}
                      placeholder={t("rsvp.childLastName")}
                      className="h-10 bg-white"
                      aria-label={`${t("rsvp.childLastName")} ${index + 1}`}
                      data-testid={`rsvp-child-${index}-last_name`}
                    />
                    <Input
                      type="number"
                      min={0}
                      max={25}
                      value={child.age}
                      onChange={(e) => setChild(index, "age", e.target.value)}
                      placeholder={t("rsvp.childAge")}
                      className="font-num h-10 w-[72px] shrink-0 bg-white"
                      aria-label={`${t("rsvp.childAge")} ${index + 1}`}
                      data-testid={`rsvp-child-${index}-age`}
                    />
                    <button
                      type="button"
                      onClick={() => setChildren((prev) => prev.filter((_, i) => i !== index))}
                      className="shrink-0 rounded-full p-1.5 text-[color:var(--muted-ink)] hover:bg-[color:var(--paper-2)]"
                      aria-label={tf("rsvp.removeChild", { n: index + 1 })}
                      data-testid={`rsvp-child-${index}-remove`}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setChildren((prev) => [...prev, { first_name: "", last_name: "", age: "" }])}
                  data-testid="rsvp-add-child"
                >
                  <Plus className="mr-1.5 h-3.5 w-3.5" /> {t("rsvp.addChild")}
                </Button>
              </div>
            ) : null}
          </div>

          <SectionTitle>{t("rsvp.tellUs")}</SectionTitle>

          {[
            { id: "dietary", label: t("rsvp.dietary"), placeholder: t("rsvp.dietaryPlaceholder"), help: t("rsvp.dietaryHint"), rows: 2 },
            { id: "interests", label: t("rsvp.interests"), placeholder: t("rsvp.interestsPlaceholder"), help: t("rsvp.interestsHint"), rows: 4 },
            { id: "seat_near", label: t("rsvp.seatNear"), placeholder: t("rsvp.seatNearPlaceholder"), rows: 2 },
            { id: "avoid", label: t("rsvp.avoid"), placeholder: t("rsvp.avoidPlaceholder"), help: t("rsvp.avoidHint"), rows: 2 },
          ].map((question) => (
            <Field key={question.id} id={question.id} label={question.label} help={question.help} optional>
              <Textarea
                id={question.id}
                value={form[question.id]}
                onChange={(e) => set(question.id, e.target.value)}
                placeholder={question.placeholder}
                rows={question.rows}
                className="mt-2 bg-white text-[14.5px]"
                data-testid={`rsvp-${question.id}-input`}
              />
            </Field>
          ))}

          <label
            className="flex cursor-pointer items-start gap-3 rounded-lg p-3"
            style={{ backgroundColor: "var(--paper-2)" }}
            data-testid="rsvp-consent"
          >
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0"
              data-testid="rsvp-consent-checkbox"
            />
            <span className="text-[12.5px] leading-relaxed text-[color:var(--ink-soft)]">
              <span className="mb-1 flex items-center gap-1.5 font-semibold" style={{ color: "var(--ink)" }}>
                <ShieldCheck className="h-3.5 w-3.5" style={{ color: "var(--sage-dark)" }} />
                {t("rsvp.yourData")}
              </span>
              {consent?.text || t("rsvp.consentFallback")}
            </span>
          </label>

          <Button
            type="submit"
            size="lg"
            disabled={saving || !agreed}
            className="h-12 w-full rounded-full text-[15px]"
            data-testid="rsvp-form-submit-button"
          >
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
            {t("rsvp.send")}
          </Button>
          <p className="flex items-center justify-center gap-1.5 text-[11.5px] text-[color:var(--muted-ink)]">
            <Heart className="h-3 w-3" style={{ color: "var(--clay)" }} />
            {t("rsvp.onlyCouple")}
          </p>
        </form>
      </div>
    </Shell>
  );
}
